import React, { useEffect, useRef, useState } from 'react';

const TerritoryMap = ({ address, radiusMiles, onAddressChange }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const circleRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [geocodedLocation, setGeocodedLocation] = useState(null);
  const [geocodeError, setGeocodeError] = useState(null);

  // Initialize map
  useEffect(() => {
    const initMap = () => {
      if (!window.google || !window.google.maps || !mapRef.current) return;

      // Default to US center if no location
      const defaultCenter = { lat: 39.8283, lng: -98.5795 };
      
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: geocodedLocation || defaultCenter,
        zoom: geocodedLocation ? 9 : 4,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      setMapLoaded(true);
    };

    // Check if Google Maps is loaded
    if (window.google && window.google.maps) {
      initMap();
    } else {
      // Wait for Google Maps to load
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          initMap();
          clearInterval(checkInterval);
        }
      }, 100);

      return () => clearInterval(checkInterval);
    }
  }, []);

  // Geocode address when it changes
  useEffect(() => {
    if (!address || address.trim().length < 5) {
      setGeocodedLocation(null);
      setGeocodeError(null);
      return;
    }

    const geocodeAddress = async () => {
      if (!window.google || !window.google.maps) return;

      const geocoder = new window.google.maps.Geocoder();
      
      try {
        geocoder.geocode({ address: address }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const location = {
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng()
            };
            setGeocodedLocation(location);
            setGeocodeError(null);
          } else {
            setGeocodeError('Could not find location');
            setGeocodedLocation(null);
          }
        });
      } catch (err) {
        setGeocodeError('Geocoding failed');
        setGeocodedLocation(null);
      }
    };

    // Debounce geocoding
    const timeoutId = setTimeout(geocodeAddress, 500);
    return () => clearTimeout(timeoutId);
  }, [address]);

  // Update marker and circle when location or radius changes
  useEffect(() => {
    if (!mapInstanceRef.current || !mapLoaded) return;

    // Clear existing marker and circle
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }

    if (geocodedLocation) {
      // Add circle for radius first (so marker appears on top)
      // Convert miles to meters (1 mile = 1609.34 meters)
      const radiusMeters = (radiusMiles || 50) * 1609.34;
      circleRef.current = new window.google.maps.Circle({
        map: mapInstanceRef.current,
        center: geocodedLocation,
        radius: radiusMeters,
        fillColor: '#3B82F6',
        fillOpacity: 0.2,
        strokeColor: '#2563EB',
        strokeOpacity: 0.9,
        strokeWeight: 3
      });

      // Add marker on top of circle
      markerRef.current = new window.google.maps.Marker({
        position: geocodedLocation,
        map: mapInstanceRef.current,
        title: 'Home Base',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#1D4ED8',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 3
        }
      });

      // Fit bounds to circle with padding
      const bounds = circleRef.current.getBounds();
      if (bounds) {
        mapInstanceRef.current.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
      }
    } else {
      // Reset to default view
      mapInstanceRef.current.setCenter({ lat: 39.8283, lng: -98.5795 });
      mapInstanceRef.current.setZoom(4);
    }
  }, [geocodedLocation, radiusMiles, mapLoaded]);

  return (
    <div className="h-full flex flex-col">
      {/* Map Container */}
      <div 
        ref={mapRef} 
        className="flex-1 rounded-lg overflow-hidden bg-gray-100"
        style={{ minHeight: '300px' }}
      />
      
      {/* Status indicator */}
      <div className="mt-3 text-center">
        {!address && (
          <p className="text-sm text-gray-500">
            Enter an address to see the coverage area
          </p>
        )}
        {address && !geocodedLocation && !geocodeError && (
          <p className="text-sm text-gray-500">
            <span className="inline-block animate-pulse">📍</span> Locating address...
          </p>
        )}
        {geocodeError && (
          <p className="text-sm text-red-600">
            ⚠️ {geocodeError}
          </p>
        )}
        {geocodedLocation && (
          <p className="text-sm text-green-600">
            ✓ Showing {radiusMiles || 50} mile coverage radius
          </p>
        )}
      </div>
    </div>
  );
};

export default TerritoryMap;
